import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { useUpdateContractorAccess } from "../hooks/useRepository";
import type { ContractorResourceAccess } from "../types";

interface Contractor {
  id: string;
  name: string;
  short_name: string;
}

function useContractors() {
  return useQuery({
    queryKey: ["contractors"],
    queryFn: () =>
      apiClient.get<{ data: Contractor[] }>("/contractors/").then((r) => r.data.data),
    staleTime: 5 * 60_000,
  });
}

interface Props {
  resourceId: string;
  access: ContractorResourceAccess;
}

export function ContractorAccessPanel({ resourceId, access }: Props) {
  const { data: contractors = [] } = useContractors();
  const { mutateAsync: updateAccess, isPending } = useUpdateContractorAccess();

  const [selectedIds, setSelectedIds] = useState<string[]>(access.contractors);

  useEffect(() => {
    setSelectedIds(access.contractors);
  }, [access.contractors]);

  const allSelected = contractors.length > 0 && contractors.every((c) => selectedIds.includes(c.id));

  function toggleAll() {
    setSelectedIds(allSelected ? [] : contractors.map((c) => c.id));
  }

  function toggle(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  async function handleSave() {
    await updateAccess({ id: resourceId, data: { contractors: selectedIds } });
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        Firmy podwykonawcze (tylko odczyt)
      </p>

      <label className="flex items-center gap-2 text-sm cursor-pointer font-medium">
        <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-[var(--color-primary)]" />
        <span className="text-gray-700 dark:text-gray-300">Zaznacz wszystkich</span>
      </label>

      <div className="max-h-56 overflow-y-auto flex flex-col gap-1 p-2">
        {contractors.map((c) => (
          <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-1 py-0.5">
            <input
              type="checkbox"
              checked={selectedIds.includes(c.id)}
              onChange={() => toggle(c.id)}
              className="accent-[var(--color-primary)]"
            />
            <span className="text-gray-700 dark:text-gray-300">{c.name}</span>
            {c.short_name && (
              <span className="text-xs text-gray-400 ml-auto">{c.short_name}</span>
            )}
          </label>
        ))}
      </div>

      <button
        onClick={handleSave}
        disabled={isPending}
        className="self-end flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
      >
        {isPending && <Loader2 size={13} className="animate-spin" />}
        Zapisz dostęp
      </button>
    </div>
  );
}
